'use client';
import React, { useState, useEffect } from 'react';
import { ModalFooter, ModalHeader, Modal } from '@/components/reactstrap';
import { useSessionContext } from 'supertokens-auth-react/recipe/session';
import { saveMeasurement, saveAnonymousMeasurement, getMeasurementsOfMember } from '@/utils/user_service';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Box,
  Typography,
  Select,
  MenuItem,
} from '@mui/material';
import Link from 'next/link';
import { CheckMark } from './icons';

function SaveModal({ isOpen, onHide, saveMeasurementObj, formData, familyMembersList = [] }) {
  const session = useSessionContext();
  const [isSaving, setIsSaving] = useState(false);
  const [saveResult, setSaveResult] = useState(null);
  const [viewMode, setViewMode] = useState('save'); // save | measurements
  const [measurements, setMeasurements] = useState([]);
  const [loadingMeasurements, setLoadingMeasurements] = useState(false);
  const [selectedMember, setSelectedMember] = useState(
    typeof window !== 'undefined' ? Number(window.sessionStorage.getItem('member_id')) || '' : '',
  );
  const [selectedChartType, setSelectedChartType] = useState('WeightChart');
  const [filteredMeasurements, setFilteredMeasurements] = useState([]);

  const measurementTypeMap = {
    WeightChart: 'WeightMeasurement',
    LengthChart: 'LengthMeasurement',
    BmiChart: 'BmiMeasurement',
    HeadChart: 'HeadMeasurement',
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveResult(null);
    try {
      let res;
      if (session.doesSessionExist) {
        res = await saveMeasurement(saveMeasurementObj.type, saveMeasurementObj);
        if (res.status === 200) {
          setSaveResult({ success: true, message: `Measurement saved to ${formData?.member_name}` });
        } else if (res.status === 401 || res.status === 403) {
          window.location.href = '/signin';
        } else {
          setSaveResult({ success: false, message: 'Oops! Problem saving your measurement.' });
        }
      } else {
        res = await saveAnonymousMeasurement(saveMeasurementObj.type, saveMeasurementObj);
        if (res.status === 200) {
          const data = await res.json();
          setSaveResult({
            success: true,
            message: 'Measurement saved anonymously! Sign up to access it later.',
            sessionId: data.session_id,
          });
        } else {
          setSaveResult({ success: false, message: 'Failed to save measurement.' });
        }
      }
    } catch (err) {
      console.error(err);
      setSaveResult({ success: false, message: 'An error occurred while saving.' });
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setViewMode('save');
      handleSave();
    }
  }, [isOpen]);

  const fetchMeasurements = async (memberId) => {
    setLoadingMeasurements(true);
    try {
      const res = await getMeasurementsOfMember(memberId);
      if (res.status === 200) {
        const data = await res.json();
        const allMeasurements = Object.values(data.family_member_data || {})
          .flat(2)
          .filter((m) => m?.id);
        setMeasurements(allMeasurements);
      } else {
        setMeasurements([]);
      }
    } catch (err) {
      console.error(err);
      setMeasurements([]);
    } finally {
      setLoadingMeasurements(false);
    }
  };

  const handleMemberChange = (e) => {
    const memberId = Number(e.target.value);
    setSelectedMember(memberId);
    if (memberId) fetchMeasurements(memberId);
  };

  useEffect(() => {
    const currentType = measurementTypeMap[selectedChartType];
    const filtered = measurements
      .filter((m) => m.type === currentType)
      .sort((a, b) => new Date(a.measure_date) - new Date(b.measure_date));
    setFilteredMeasurements(filtered);
  }, [selectedChartType, measurements]);

  useEffect(() => {
    if (isOpen && selectedMember) {
      const timeout = setTimeout(() => {
        fetchMeasurements(selectedMember);
      }, 400);
      return () => clearTimeout(timeout);
    }
  }, [isOpen, selectedMember]);

  return (
    <Modal centered isOpen={isOpen} onHide={onHide} className="save-modal">
      <ModalHeader className="border-0">
        {viewMode === 'save' ? 'Measurement Saved' : 'Previous Measurements'}
        <button className="close-btn" onClick={onHide}>
          <img priority src="/assets/images/icons/cross.svg" alt="cross" />
        </button>
      </ModalHeader>

      {viewMode === 'save' && (
        <>
          {saveResult ? (
            <Box textAlign="center" px={3}>
              <Typography variant="body1" color={saveResult.success ? 'success.main' : 'error.main'} sx={{ my: 2 }}>
                {saveResult.message}
              </Typography>

              {/* Show feature list if user is not signed in */}
              {!session.doesSessionExist && (
                <div>
                  <ul style={{ textAlign: 'left', display: 'inline-block', marginTop: '10px' }}>
                    <li>
                      <CheckMark />
                      <span className="ms-2">Free forever</span>
                    </li>
                    <li>
                      <CheckMark />
                      <span className="ms-2">Set Personal Reminders</span>
                    </li>
                    <li>
                      <CheckMark />
                      <span className="ms-2">Chart Growth Over Time</span>
                    </li>
                    <li>
                      <CheckMark />
                      <span className="ms-2">Track All Family Members</span>
                    </li>
                  </ul>
                </div>
              )}
            </Box>
          ) : session.doesSessionExist ? (
            <Box textAlign="center" px={3}>
              Do you want to see all your measurements so far?
            </Box>
          ) : null}

          {session.doesSessionExist ? (
            <ModalFooter className="border-0 justify-content-center">
              <button
                className="btn rounded-pill bg-transparent text-dark w-50 py-2"
                onClick={onHide}
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary w-50 py-2"
                onClick={() => setViewMode('measurements')}
                disabled={isSaving}
                style={{ fontSize: '17px' }}
              >
                {isSaving ? 'Saving...' : 'View Saved Measurements'}
              </button>
            </ModalFooter>
          ) : (
            <ModalFooter className="border-0 justify-content-center">
              <Link href="/signup" style={{ cursor: 'pointer' }} prefetch={false}>
                <button color="btn btn-primary" style={{ width: '200px', minWidth: 'unset' }}>
                  Sign Up
                </button>
              </Link>
            </ModalFooter>
          )}
        </>
      )}

      {viewMode === 'measurements' && (
        <Box px={3}>
          <Box mb={3} display="flex" gap={2}>
            {/* Family Member Selector */}
            <Select
              value={selectedMember}
              onChange={handleMemberChange}
              displayEmpty
              sx={{ flex: 1, minHeight: 36 }}
              size="small"
            >
              <MenuItem value="">Select a family member</MenuItem>
              {familyMembersList?.map((el) => (
                <MenuItem key={el.id} value={el.id}>
                  {el.name}
                </MenuItem>
              ))}
            </Select>

            {/* Chart Type Selector */}
            <Select
              value={selectedChartType}
              onChange={(e) => setSelectedChartType(e.target.value)}
              sx={{ flex: 1, minHeight: 36 }}
              size="small"
            >
              <MenuItem value="WeightChart">Weight</MenuItem>
              <MenuItem value="LengthChart">Length</MenuItem>
              <MenuItem value="BmiChart">BMI</MenuItem>
              <MenuItem value="HeadChart">Head</MenuItem>
            </Select>
          </Box>
          {loadingMeasurements ? (
            <Box textAlign="center" my={4}>
              <CircularProgress />
            </Box>
          ) : filteredMeasurements.length > 0 ? (
            <Box>
              <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Age</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Value</TableCell>
                      <TableCell>Percentile</TableCell>
                      <TableCell>Measured On</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredMeasurements.map((m) => (
                      <TableRow key={m.id}>
                        <TableCell>{formatAge(m.age)}</TableCell>
                        <TableCell>{m.type.replace('Measurement', '')}</TableCell>
                        <TableCell>{formatMeasurementValue(m.value, m.type)}</TableCell>
                        <TableCell>{m.percentile}</TableCell>
                        <TableCell>{new Date(m.measure_date).toLocaleDateString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ) : (
            <Typography align="center" color="text.secondary" sx={{ my: 2 }}>
              No data found for this chart type.
            </Typography>
          )}

          <ModalFooter className="border-0 justify-content-center mt-3">
            <button className="btn rounded-pill bg-transparent text-dark w-50 py-2" onClick={() => setViewMode('save')}>
              Back
            </button>
          </ModalFooter>
        </Box>
      )}
    </Modal>
  );
}

export default SaveModal;

function formatAge(months) {
  if (typeof months !== 'number' || months < 0) return '';
  const years = Math.floor(months / 12);
  const remainingMonths = months - years * 12;
  return remainingMonths === 0 ? `${years}y` : `${years}y-${remainingMonths}m`;
}

function formatMeasurementValue(value, type) {
  if (typeof value !== 'number') return '';
  switch (type) {
    case 'LengthMeasurement':
    case 'HeadMeasurement':
      return `${value} cm`;
    case 'WeightMeasurement':
      return `${value} kg`;
    case 'BmiMeasurement':
      return value;
    default:
      return value;
  }
}
